import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// material-ui
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';

// project imports
import Loader from '@/components/Loader';
import Breadcrumbs from '@/components/@extended/Breadcrumbs';

import { MenuOrientation } from '@/theme/config';
import useConfig from '@/theme/useConfig';
import { handlerDrawerOpen, useGetMenuMaster } from '@/api/menu';
import Header from './Header';
import Drawer from './Drawer';

interface DashboardLayoutProps {
	children?: ReactNode;
}

// ==============================|| MAIN LAYOUT ||============================== //

// Access control is handled by the existing route guards (AdminRoute/AuthRoute/etc.),
// so this layout only provides the visual shell and renders the routed page via `children`.
export default function DashboardLayout({ children }: DashboardLayoutProps) {
	const { menuMasterLoading } = useGetMenuMaster();
	const downXL = useMediaQuery((theme: Theme) => theme.breakpoints.down('xl'));
	const { pathname } = useLocation();
	const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
	const isChatWorkspace = normalizedPathname === '/common/chat';

	const { state } = useConfig();

	// set media wise responsive drawer
	useEffect(() => {
		if (state.menuOrientation !== MenuOrientation.MINI_VERTICAL) {
			handlerDrawerOpen(!downXL);
		}
	}, [downXL]);

	if (menuMasterLoading) return <Loader />;

	return (
		<Stack direction="row" sx={{ width: 1 }}>
			<Header />
			<Drawer />

			<Box
				component="main"
				sx={{
					width: 'calc(100% - 260px)',
					flexGrow: 1,
					display: 'flex',
					flexDirection: 'column',
					p: isChatWorkspace ? 0 : { xs: 2, sm: 5 },
					pt: 0,
					bgcolor: 'background.default',
					// body is locked to the viewport (index.css), so the content region must scroll
					// internally instead of growing past the viewport and getting clipped.
					height: '100vh',
					overflowY: isChatWorkspace ? 'hidden' : 'auto'
				}}
			>
				<Toolbar sx={{ mt: 'inherit', minHeight: isChatWorkspace ? '60px' : '48px' }} />
				<Container
					maxWidth={false}
					disableGutters
					sx={{
						position: 'relative',
						width: '100%',
						minHeight: isChatWorkspace ? 0 : 'calc(100vh - 150px)',
						maxWidth: isChatWorkspace ? 'none' : '1440px',
						flex: isChatWorkspace ? 1 : undefined,
						display: 'flex',
						flexDirection: 'column',
						overflow: isChatWorkspace ? 'hidden' : undefined
					}}
				>
					{/* flexShrink 0 — altfel coloana flex comprimă cardul (overflow hidden) și taie titlul */}
					{!isChatWorkspace && <Breadcrumbs sx={{ flexShrink: 0 }} />}
					{children}
				</Container>
			</Box>
		</Stack>
	);
}
